import { TweetAuthorAffiliationSchema, type TweetLinkMetadata } from '@sweetistics/contracts/tweets-canonical';
import type { TimelineSort } from '@/domains/timeline/lib';
import { buildTweetAuthorPreview, extractPublicMetrics } from '@/domains/timeline/lib/transformers/profile';
import {
  hasMeaningfulPreview,
  primeUserPreviewCacheFromPreview,
  resolveUserPreviewCacheKeys,
  upsertUserPreviewCacheEntry,
} from '@/lib/cache/user-preview-cache';
import { normalizeTweetMap } from '@/lib/twitter/normalize-tweet';
import { toCanonicalLinkMetadataRecord } from '@/lib/url-metadata/normalize';
import { ensureDate } from '@/lib/utils/date-coercion';
import { filter, map } from '@/lib/utils/es-toolkit-helpers';
import { stripHandlePrefixes } from '@/lib/utils/regex';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type { TimelinePageResponse } from '@/lib/validation/api';
import type { UserPreviewVM } from '@/lib/view-models/user-preview';
import type { Tweet, TweetAuthor } from '@/types/tweet';

type TimelineProfilesRecord = TimelinePageResponse['profiles'];
type TimelineUrlMetadataRecord = TimelinePageResponse['urlMetadata'];
type TimelineMentionProfilesRecord = NonNullable<TimelinePageResponse['mentionProfiles']>;

export type TimelineViewMode = 'timeline' | 'topics' | 'mentions' | 'algorithmic' | 'lists';
export type TimelineContentFilter = 'all' | 'links' | 'media';
export type TimelineTabsAvailability = {
  mentions: boolean;
  topics: boolean;
  algorithmic: boolean;
};

const URL_FALLBACK_PATTERN = /https?:\/\/\S+/i;

const toUserPreviewFromTimelineProfile = (value: unknown): UserPreviewVM | null => {
  const author = buildTweetAuthorPreview(value);
  if (!author) {
    return null;
  }
  const preview: UserPreviewVM = {
    id: author.id,
    username: author.username,
    name: author.name,
    verifiedType: author.verifiedType,
  };
  if (author.profileImageUrl !== undefined) {
    preview.profileImageUrl = author.profileImageUrl ?? null;
  }
  const metrics = author.publicMetrics;
  if (typeof metrics?.followersCount === 'number') {
    preview.followersCount = metrics.followersCount;
  }
  if (typeof metrics?.followingCount === 'number') {
    preview.followingCount = metrics.followingCount;
  }
  if (typeof metrics?.tweetCount === 'number') {
    preview.tweetCount = metrics.tweetCount;
  }
  if (typeof metrics?.listedCount === 'number') {
    preview.listedCount = metrics.listedCount;
  }
  return preview;
};

function primeTimelineProfiles(page: TimelinePageResponse) {
  for (const profile of Object.values(page.profiles)) {
    const preview = toUserPreviewFromTimelineProfile(profile);
    if (!preview) {
      continue;
    }
    primeUserPreviewCacheFromPreview(preview);
  }

  if (!page.mentionProfiles) {
    return;
  }

  for (const mention of Object.values(page.mentionProfiles)) {
    const preview = toUserPreviewFromTimelineProfile(mention);
    if (!preview) {
      continue;
    }
    const keys = resolveUserPreviewCacheKeys({
      profileId: preview.id,
      username: preview.username,
    });
    if (hasMeaningfulPreview(preview)) {
      for (const key of keys) {
        upsertUserPreviewCacheEntry(key, {
          preview,
          error: false,
          detail: 'partial',
        });
      }
    }
  }
}

const AUTHOR_CREATED_AT_FALLBACK = ensureDate(0);

function attachAuthor(base: Tweet, profiles: TimelineProfilesRecord): void {
  if (!base.authorId) {
    return;
  }

  const profileRecord = profiles[base.authorId] ?? null;
  const existing = base.author;

  if (!(profileRecord || existing)) {
    return;
  }

  const normalizedProfile = profileRecord ? buildTweetAuthorPreview({ ...profileRecord, id: base.authorId }) : null;
  const merged: TweetAuthor = existing
    ? { ...existing }
    : {
        id: base.authorId,
        username: 'unknown',
        name: 'Unknown',
        verifiedType: 'none',
        createdAt: AUTHOR_CREATED_AT_FALLBACK,
      };

  merged.id = base.authorId;
  merged.username = merged.username ?? 'unknown';
  merged.name = merged.name ?? 'Unknown';
  merged.verifiedType = merged.verifiedType ?? 'none';
  merged.createdAt = merged.createdAt ?? AUTHOR_CREATED_AT_FALLBACK;

  if (normalizedProfile) {
    merged.username = normalizedProfile.username;
    merged.name = normalizedProfile.name;
    merged.verifiedType = normalizedProfile.verifiedType;
    merged.profileImageUrl = normalizedProfile.profileImageUrl ?? merged.profileImageUrl;
    merged.verified = normalizedProfile.verified ?? merged.verified;
    merged.publicMetrics = normalizedProfile.publicMetrics ?? merged.publicMetrics;
    merged.createdAt = normalizedProfile.createdAt ?? merged.createdAt;
  }

  if (profileRecord) {
    if (typeof profileRecord.description === 'string') {
      merged.description = profileRecord.description;
    }
    if (Array.isArray(profileRecord.affiliations)) {
      const affiliationsResult = TweetAuthorAffiliationSchema.array().safeParse(profileRecord.affiliations);
      if (affiliationsResult.success) {
        merged.affiliations = affiliationsResult.data;
      }
    }
    const rawPrimaryAffiliation = profileRecord.primaryAffiliation;
    if (rawPrimaryAffiliation && typeof rawPrimaryAffiliation.organizationProfileId === 'string') {
      merged.primaryAffiliation = {
        organizationProfileId: rawPrimaryAffiliation.organizationProfileId,
        organizationName: toTrimmedNonEmptyString(rawPrimaryAffiliation.organizationName) ?? undefined,
        organizationUsername: toTrimmedNonEmptyString(rawPrimaryAffiliation.organizationUsername) ?? undefined,
        badgeUrl: typeof rawPrimaryAffiliation.badgeUrl === 'string' ? rawPrimaryAffiliation.badgeUrl : undefined,
        url: typeof rawPrimaryAffiliation.url === 'string' ? rawPrimaryAffiliation.url : undefined,
      } satisfies NonNullable<TweetAuthor['primaryAffiliation']>;
    }
    const normalizedMetrics = extractPublicMetrics(profileRecord.publicMetrics);
    if (normalizedMetrics) {
      merged.publicMetrics = normalizedMetrics;
    }
  }

  base.author = merged;
  profiles[base.authorId] = merged;
}

function attachUrlMetadata(base: Tweet, urlMetadata: TimelineUrlMetadataRecord): void {
  if (!base.entities?.urls || base.entities.urls.length === 0) {
    return;
  }
  const normalized = toCanonicalLinkMetadataRecord(urlMetadata);
  if (!normalized) {
    return;
  }
  const existingMetadata: Record<string, TweetLinkMetadata> | undefined = base.metadata
    ? { ...base.metadata }
    : undefined;
  const metadata: Record<string, TweetLinkMetadata> = existingMetadata ? { ...existingMetadata } : {};
  for (const entry of base.entities.urls) {
    const expandedUrl = entry.expandedUrl || entry.url;
    if (!expandedUrl) {
      continue;
    }
    const meta = normalized[expandedUrl];
    if (meta) {
      metadata[expandedUrl] = meta;
    }
  }
  if (Object.keys(metadata).length > 0) {
    base.metadata = metadata;
  } else if (existingMetadata) {
    base.metadata = existingMetadata;
  }
}

function attachMentionMetadata(base: Tweet, mentionProfiles: TimelineMentionProfilesRecord | undefined): void {
  if (!(mentionProfiles && Array.isArray(base.entities?.mentions))) {
    return;
  }

  for (const mention of base.entities.mentions) {
    if (!mention || typeof mention.username !== 'string') {
      continue;
    }
    const normalized = stripHandlePrefixes(mention.username).trim().toLowerCase();
    if (!normalized) {
      continue;
    }
    const entry = mentionProfiles[normalized];
    if (!entry) {
      continue;
    }
    if (!mention.id && typeof entry.profileId === 'string') {
      mention.id = entry.profileId;
    }
    if (!mention.name && typeof entry.name === 'string') {
      mention.name = entry.name;
    }
    if (!mention.profileImageUrl && typeof entry.profileImageUrl === 'string') {
      mention.profileImageUrl = entry.profileImageUrl;
    }
  }
}

function enrichNestedTweet(
  nested: Tweet | undefined,
  profiles: TimelineProfilesRecord,
  urlMetadata: TimelineUrlMetadataRecord,
  mentionProfiles: TimelineMentionProfilesRecord | undefined
): void {
  if (!nested) {
    return;
  }
  attachAuthor(nested, profiles);
  attachUrlMetadata(nested, urlMetadata);
  attachMentionMetadata(nested, mentionProfiles);
}

function enrichTweet(
  base: Tweet,
  profiles: TimelineProfilesRecord,
  urlMetadata: TimelineUrlMetadataRecord,
  mentionProfiles: TimelineMentionProfilesRecord | undefined
): void {
  attachAuthor(base, profiles);
  attachUrlMetadata(base, urlMetadata);
  attachMentionMetadata(base, mentionProfiles);
  enrichNestedTweet(base.retweetedTweet ?? undefined, profiles, urlMetadata, mentionProfiles);
  enrichNestedTweet(base.quotedTweet ?? undefined, profiles, urlMetadata, mentionProfiles);
}

// Walk nested quote/retweet chains to detect attributes without risking cycles.
function traverseTweetTree(
  tweet: Tweet | undefined,
  matcher: (candidate: Tweet) => boolean,
  visited: Set<string>
): boolean {
  if (!tweet) {
    return false;
  }

  if (visited.has(tweet.id)) {
    return false;
  }
  visited.add(tweet.id);

  if (matcher(tweet)) {
    return true;
  }

  return (
    traverseTweetTree(tweet.retweetedTweet ?? undefined, matcher, visited) ||
    traverseTweetTree(tweet.quotedTweet ?? undefined, matcher, visited)
  );
}

export function tweetIsRetweet(tweet: Tweet): boolean {
  if (tweet.isRetweet) {
    return true;
  }
  if (tweet.retweetedTweet) {
    return true;
  }
  return Boolean(tweet.referencedTweets?.some((reference) => reference?.type === 'retweeted'));
}

export function tweetIsReply(tweet: Tweet): boolean {
  if (tweet.inReplyToUserId) {
    return true;
  }
  return Boolean(tweet.referencedTweets?.some((reference) => reference?.type === 'repliedTo'));
}

export function tweetHasLinks(tweet: Tweet): boolean {
  return traverseTweetTree(
    tweet,
    (candidate) => {
      if (candidate.entities?.urls && candidate.entities.urls.length > 0) {
        return true;
      }
      if (candidate.metadata && Object.keys(candidate.metadata).length > 0) {
        return true;
      }
      if (candidate.text && URL_FALLBACK_PATTERN.test(candidate.text)) {
        return true;
      }
      if (candidate.noteTweet?.text && URL_FALLBACK_PATTERN.test(candidate.noteTweet.text)) {
        return true;
      }
      return false;
    },
    new Set()
  );
}

export function tweetHasMedia(tweet: Tweet): boolean {
  return traverseTweetTree(
    tweet,
    (candidate) => {
      if (candidate.mediaItems && candidate.mediaItems.length > 0) {
        return true;
      }
      if (candidate.media && Array.isArray(candidate.media) && candidate.media.length > 0) {
        return true;
      }
      if (candidate.attachments?.mediaItems && candidate.attachments.mediaItems.length > 0) {
        return true;
      }
      if (candidate.attachments?.mediaKeys && candidate.attachments.mediaKeys.length > 0) {
        return true;
      }
      return false;
    },
    new Set()
  );
}

export function isTweetLike(tweet: unknown): tweet is Tweet {
  return (
    tweet !== undefined &&
    tweet !== null &&
    typeof tweet === 'object' &&
    'id' in tweet &&
    'text' in tweet &&
    'authorId' in tweet &&
    'createdAt' in tweet &&
    typeof (tweet as { id: unknown }).id === 'string' &&
    typeof (tweet as { text: unknown }).text === 'string' &&
    typeof (tweet as { authorId: unknown }).authorId === 'string' &&
    (tweet as { createdAt: unknown }).createdAt instanceof Date
  );
}

export function mergeLocalTweets(baseTweets: Tweet[], localTweets: Tweet[]): Tweet[] {
  if (localTweets.length === 0) {
    return baseTweets;
  }

  const baseIds = new Set(map(baseTweets, (t: Tweet) => t.id));
  const uniqueLocal = filter(localTweets, (tweet: Tweet) => !baseIds.has(tweet.id));

  if (uniqueLocal.length === 0) {
    return baseTweets;
  }

  return [...uniqueLocal, ...baseTweets];
}

export function buildVisibleTweets({
  sortMode,
  displayedTweets,
  tweetsInSelectedRange,
  viewMode,
}: {
  sortMode: TimelineSort;
  displayedTweets: Tweet[];
  tweetsInSelectedRange: Tweet[];
  viewMode: TimelineViewMode;
}): Tweet[] {
  let baseList: Tweet[];
  if (tweetsInSelectedRange.length > 0) {
    baseList = tweetsInSelectedRange;
  } else if (sortMode === 'latest' || viewMode === 'mentions') {
    baseList = displayedTweets;
  } else {
    baseList = [];
  }

  if (baseList.length === 0) {
    return [];
  }

  const sorted = [...baseList];

  if (sortMode === 'latest') {
    sorted.sort((a, b) => {
      const diff = b.createdAt.getTime() - a.createdAt.getTime();
      if (diff !== 0) {
        return diff;
      }
      return b.id.localeCompare(a.id);
    });
    return sorted;
  }

  const metricFor = (tweet: Tweet): number => {
    if (sortMode === 'views') {
      return tweet.impressionCount ?? 0;
    }
    if (sortMode === 'likes') {
      return tweet.likeCount ?? 0;
    }
    if (sortMode === 'bookmarks') {
      return tweet.bookmarkCount ?? 0;
    }
    return 0;
  };

  sorted.sort((a, b) => {
    const diff = metricFor(b) - metricFor(a);
    if (diff !== 0) {
      return diff;
    }
    const createdDiff = b.createdAt.getTime() - a.createdAt.getTime();
    if (createdDiff !== 0) {
      return createdDiff;
    }
    return b.id.localeCompare(a.id);
  });

  return sorted;
}

export function buildTweetsFromPages(pages: TimelinePageResponse[]): Tweet[] {
  const toTweets = (page: TimelinePageResponse): Tweet[] => {
    if (!(page.timelineTweetIds && page.tweets)) return [];

    const tweetsMap = normalizeTweetMap(page.tweets, {
      context: { source: 'timeline.helpers.pages' },
      suppressLogging: true,
    });

    const profilesMap: TimelineProfilesRecord = page.profiles;
    const urlMetadataMap: TimelineUrlMetadataRecord = page.urlMetadata;

    primeTimelineProfiles(page);

    const mentionProfilesMap: TimelineMentionProfilesRecord | undefined = page.mentionProfiles ?? undefined;
    const enriched: Tweet[] = [];
    for (const id of page.timelineTweetIds) {
      const tweet = tweetsMap[id];
      if (!tweet) {
        continue;
      }
      enrichTweet(tweet, profilesMap, urlMetadataMap, mentionProfilesMap);
      enriched.push(tweet);
    }
    return enriched;
  };

  return pages.flatMap((pageEntry) => toTweets(pageEntry));
}

// Resolve the canonical tweet id to hydrate a conversation for a given timeline entry. We prefer
// the original tweet id for reshares so the conversation fetch pulls the full upstream thread
// instead of the shallow retweet wrapper.
export function resolveConversationTargetTweetId(tweet: Tweet | null | undefined): string | null {
  if (!tweet) {
    return null;
  }

  if (tweet.retweetedTweet?.id) {
    return tweet.retweetedTweet.id;
  }

  const retweetReference = tweet.referencedTweets?.find(
    (reference) => reference.type === 'retweeted' && typeof reference.id === 'string'
  );
  if (retweetReference?.id) {
    return retweetReference.id;
  }

  return tweet.id;
}
